import {
  AsyncValidatorFn,
  FormArray,
  FormControl,
  FormControlOptions,
  FormControlState,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';
import { Observable, Subject } from 'rxjs';

export type ExtractControls<Type> = {
  [Property in keyof Type]-?: Type[Property] extends Array<unknown>
    ? FormArray<FormGroup<ExtractControls<Type[Property][number]>>>
    : FormControl<Type[Property]>;
};

export interface EnhancedFormControl<TValue = unknown> extends FormControl<TValue> {
  touchedChanges: Observable<boolean>;
  enabledChanges: Observable<boolean>;
}

interface ɵEnhancedFormControlCtor {
  new (): EnhancedFormControl<unknown>;

  new <T = unknown>(
    value: FormControlState<T> | T,
    opts: FormControlOptions & { nonNullable: true },
  ): EnhancedFormControl<T>;

  new <T = unknown>(
    value: FormControlState<T> | T,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | FormControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null,
  ): EnhancedFormControl<T>;

  prototype: EnhancedFormControl<unknown>;
}

export const EnhancedFormControl: ɵEnhancedFormControlCtor = class EnhancedFormControl<
  TValue = unknown,
> extends FormControl {
  private touchedChangesSubj = new Subject<boolean>();
  touchedChanges = this.touchedChangesSubj.asObservable();

  private enabledChangesSubj = new Subject<boolean>();
  enabledChanges = this.enabledChangesSubj.asObservable();

  constructor(
    formState: FormControlState<TValue> | TValue = undefined as unknown as TValue,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | FormControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null,
  ) {
    super(formState, validatorOrOpts, asyncValidator);
  }

  override enable(opts?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    super.enable(opts);
    this.enabledChangesSubj.next(true);
  }

  override disable(opts?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    super.disable(opts);
    this.enabledChangesSubj.next(false);
  }

  override markAsTouched(opts?: { onlySelf?: boolean }): void {
    if (!this.touched) {
      super.markAsTouched(opts);
      this.touchedChangesSubj.next(true);
    }
  }

  override markAsUntouched(opts?: { onlySelf?: boolean }): void {
    if (this.touched) {
      // we need set it as untouched before sending event, so event receiver
      // will se new state of control
      super.markAsUntouched(opts);
      this.touchedChangesSubj.next(false);
    }
  }
};
